import React, { useEffect, useMemo, useState } from 'react';
import { SketchPicker } from 'react-color';

const DEFAULT_COLOR = '#000000';

const isValidHexColor = (value) => {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(v);
};

const normalizeHexColor = (value, fallback = DEFAULT_COLOR) => {
  if (isValidHexColor(value)) return value.trim();
  return fallback;
};

const ColorPicker = ({ color, onChange, label }) => {
  const [showPicker, setShowPicker] = useState(false);
  const safeColor = useMemo(() => normalizeHexColor(color, DEFAULT_COLOR), [color]);
  const [draft, setDraft] = useState(typeof color === 'string' ? color : safeColor);

  useEffect(() => {
    // Keep the input in sync with external updates,
    // but never allow null/undefined to propagate to the picker.
    setDraft(typeof color === 'string' ? color : safeColor);
  }, [color, safeColor]);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded border-2 border-gray-300 dark:border-gray-600 cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
          style={{ backgroundColor: safeColor,borderWidth:20,borderColor:safeColor}}
          onClick={() => setShowPicker(!showPicker)}
        />
        <input
          type="text"
          value={draft ?? ''}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            // Only commit valid values. This prevents downstream crashes.
            if (isValidHexColor(next)) onChange(next.trim());
          }}
          onBlur={() => {
            // Revert any invalid draft so callers don't end up with null/invalid.
            if (!isValidHexColor(draft)) setDraft(safeColor);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            if (isValidHexColor(draft)) {
              onChange(draft.trim());
              setShowPicker(false);
            } else {
              setDraft(safeColor);
            }
          }}
          className="flex-1 px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="#000000"
        />
      </div>
      {showPicker && (
        <div className="absolute z-50 mt-2">
          <div
            className="fixed inset-0"
            onClick={() => setShowPicker(false)}
          />
          <div className="relative">
            <SketchPicker
              color={safeColor}
              onChangeComplete={(colorObj) => {
                const nextHex = colorObj?.hex;
                if (isValidHexColor(nextHex)) onChange(nextHex.trim());
                setShowPicker(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;

